import React, { useEffect, useRef, useState } from 'react';
import { Quote, Star, MapPin } from 'lucide-react';
import { REFERENCES_STATS, TESTIMONIALS, IMG } from '../mock/mock';
import { LeafTopLeft, LeafBottomRight } from './Leaves';

function useCountUp(target, start) {
  const [val, setVal] = useState(target);
  useEffect(() => {
    if (!start) { setVal(target); return; }
    setVal(0);
    const dur = 1500;
    const t0 = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      setVal(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, start]);
  return val;
}

function StatItem({ s, visible }) {
  const v = useCountUp(s.value, visible);
  return (
    <div className="text-center">
      <div className="font-serif-display text-6xl md:text-8xl text-[#2BA8C0] mb-2 leading-none">
        {v}{s.suffix}
      </div>
      <div className="text-[12px] uppercase tracking-[0.2em] text-white/55 font-medium">{s.label}</div>
    </div>
  );
}

export default function References() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.3 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="referenciak" className="relative py-24 md:py-32 bg-gradient-to-b from-[#0e2030] via-[#0d2336] to-[#0a1d2e] text-white overflow-hidden">
      {/* Top fade from white packages section */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-white/15 to-transparent pointer-events-none" />
      {/* Leaf corners */}
      <LeafTopLeft className="absolute top-0 left-0 w-40 md:w-48 h-auto pointer-events-none opacity-60 z-[5]" />
      <LeafBottomRight className="absolute bottom-0 right-0 w-40 md:w-48 h-auto pointer-events-none opacity-60 z-[5]" />
      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="section-num text-[#2BA8C0] text-2xl">05</span>
            <span className="text-[12px] uppercase tracking-[0.3em] text-white/60 font-medium">Referenciák</span>
          </div>
          <h2 className="font-serif-display text-4xl md:text-6xl text-white leading-[1.05] max-w-4xl mx-auto">
            A Velencei-tónál<br /><span className="italic text-[#2BA8C0]">már otthon vannak</span>
          </h2>
        </div>

        <div ref={ref} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
          {REFERENCES_STATS.map((s) => (
            <div key={s.label} className="py-12 md:py-16">
              <StatItem s={s} visible={visible} />
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 space-y-5">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="relative bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-10">
                <Quote className="absolute top-7 right-7 w-8 h-8 text-white/15" />
                <div className="flex items-center gap-1 mb-5">
                  {[...Array(5)].map((_, k) => (
                    <Star key={k} className="w-4 h-4 text-[#2BA8C0] fill-[#2BA8C0]" />
                  ))}
                </div>
                <p className="text-white/85 text-[15px] leading-relaxed mb-6 italic">&ldquo;{t.text}&rdquo;</p>
                <div className="flex items-center gap-4 pt-5 border-t border-white/10">
                  <div className="w-11 h-11 rounded-full bg-[#2BA8C0]/15 border border-[#2BA8C0]/30 text-[#2BA8C0] flex items-center justify-center font-serif-display text-lg">
                    {t.initial}
                  </div>
                  <div>
                    <div className="text-white font-medium text-[15px]">{t.name}</div>
                    <div className="text-[12px] uppercase tracking-wider text-white/50">{t.place}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-5 relative rounded-3xl overflow-hidden min-h-[400px] lg:min-h-full">
            <img src={IMG.sunset} alt="Velencei-tó napnyugtakor" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute bottom-5 left-5 bg-white px-3.5 py-1.5 rounded-full flex items-center gap-2">
              <MapPin className="w-3 h-3 text-[#2BA8C0]" />
              <span className="text-[11px] uppercase tracking-wider text-[#1a1a1a] font-semibold">Velencei-tó</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
